import { Card } from '@/types/game';
import { DEFAULT_SETTINGS, GameSettings, normalizeSettings, PropKind, MAIN_PROP_KINDS } from '@/data/settings';
import { QUESTIONS, questionsIn } from '@/data/questions';
import { BoardMode } from '@/data/board';
import { BOARD_PATTERNS, normalizePatternIndex, resolvePatternIndex } from '@/data/boardPatterns';
import { generateCards, checkCover, generateId } from './game';
import { seededRandom } from './random';
import { buildModeCards, chinaDateKey, modeSeed } from './modeBoards';
import { reserveGroupsOf, reserveColumnsOf, reserveStacksOf } from './staging';

export type GamePhase = 'ready' | 'playing' | 'paused' | 'failed' | 'won' | 'quiz';
export type RunOutcome = 'win' | 'fail' | 'abandon';
export type PropBalances = Record<PropKind, number>;
export const DEFAULT_PROP_BALANCES: PropBalances = { remove: 1, undo: 1, shuffle: 1, revive: 1 };
export const SLOT_SIZE = 7;
export const RULES_VERSION = 'sheep-prop-sets-4';
export const GENERATOR_VERSION = 'historical-source-geometry-7';

interface Snapshot { cards: Card[]; slotCardIds: string[]; reserveCardIds?: string[]; reserveGroups?: string[][]; reserveColumns?: Record<string, number>; score: number }
export interface SettledRun { runId: string; outcome: RunOutcome; level: number; score: number; elapsedMs: number;
  boardMode?: BoardMode; dayKey?: string; worldStage?: number }
export interface QuizSession {
  id: string; runId: string; revision: number; kind: PropKind; questionId: string;
  returnPhase: 'playing' | 'failed'; needsGrant: boolean; attempts: number;
  selected: number | null; feedback: 'wrong' | 'correct' | null; round: number;
}
export interface GameState {
  runId: string; level: number; phase: GamePhase; cards: Card[];
  slotCardIds: string[]; reserveCardIds: string[]; reserveGroups: string[][]; reserveColumns: Record<string, number>; score: number;
  props: PropBalances; initialProps: PropBalances; settings: GameSettings;
  seed: number; revision: number; elapsedMs: number; lastResumeAt: number | null;
  history: Snapshot[]; failSnapshot: Snapshot | null;
  settledOutcome: RunOutcome | null; lastSettledRun: SettledRun | null;
  quiz: QuizSession | null;
  quizStats: { firstAttempts: number; firstCorrect: number; completed: number; grants: number; grantsByKind?: Partial<Record<PropKind, number>> };
  boardMode?: BoardMode; dayKey?: string; worldStage?: number; layoutIndex?: number; layoutId?: string;
}
interface ModeRun { mode: BoardMode; dayKey: string; stage: number; layoutIndex?: number; layoutId?: string }
export const quizGrantLimit = (settings: GameSettings, kind: PropKind): number => kind === 'revive' ? settings.reviveQuizLimit : settings.quizLimit;
/** Keep old prop usage; independent revival starts at zero when older saves did not track it. */
export const quizGrantUsage = (stats?: GameState['quizStats']): Record<PropKind, number> => {
  const count = (value: unknown, fallback = 0) => typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.floor(value) : fallback;
  return { ...Object.fromEntries(MAIN_PROP_KINDS.map(kind => [kind, count(stats?.grantsByKind?.[kind], count(stats?.grants))])),
    revive: count(stats?.grantsByKind?.revive) } as Record<PropKind, number>;
};
const snapshotOf = (s: GameState): Snapshot => ({
  // Reducers never mutate existing cards; immutable history avoids copying a large board on every tap.
  cards: s.cards, slotCardIds: [...s.slotCardIds],
  reserveCardIds: [...s.reserveCardIds], reserveGroups: reserveGroupsOf(s.reserveCardIds, s.reserveGroups),
  reserveColumns: reserveColumnsOf(s.reserveCardIds, s.reserveGroups, s.reserveColumns), score: s.score
});
const restoreSnapshot = (s: Snapshot) => ({
  cards: checkCover(s.cards.map(c => ({ ...c }))), slotCardIds: [...s.slotCardIds],
  reserveCardIds: [...(s.reserveCardIds || [])], reserveGroups: reserveGroupsOf(s.reserveCardIds || [], s.reserveGroups),
  reserveColumns: reserveColumnsOf(s.reserveCardIds || [], s.reserveGroups, s.reserveColumns), score: s.score
});
export const shuffledWithRng = <T>(arr: T[], rng: () => number): T[] => {
  const res = arr.slice();
  for (let i = 0; i < res.length - 1; i++) {
    const j = i + Math.floor(rng() * (res.length - i));
    [res[i], res[j]] = [res[j], res[i]];
  }
  return res;
};
const syncClock = (s: GameState, now: number): GameState =>
  s.phase !== 'playing' || s.lastResumeAt === null ? s :
    { ...s, elapsedMs: s.elapsedMs + Math.max(0, now - s.lastResumeAt), lastResumeAt: now };
export const effectiveElapsedMs = (s: GameState, now: number): number =>
  s.elapsedMs + (s.phase === 'playing' && s.lastResumeAt !== null ? Math.max(0, now - s.lastResumeAt) : 0);
export const createIdleState = (): GameState => ({
  runId: '', level: 1, phase: 'ready', cards: [], slotCardIds: [], reserveCardIds: [], reserveGroups: [], reserveColumns: {},
  score: 0, props: { ...DEFAULT_PROP_BALANCES }, initialProps: { ...DEFAULT_PROP_BALANCES },
  settings: normalizeSettings(DEFAULT_SETTINGS), seed: 0, revision: 0,
  elapsedMs: 0, lastResumeAt: null, history: [], failSnapshot: null,
  settledOutcome: null, lastSettledRun: null, quiz: null,
  quizStats: { firstAttempts: 0, firstCorrect: 0, completed: 0, grants: 0, grantsByKind: { remove: 0, undo: 0, shuffle: 0, revive: 0 } }
});
export const startRun = (
  level: number, cardTypes: string[], now?: number, balances?: PropBalances,
  options: GameSettings = DEFAULT_SETTINGS, seed = Math.floor(Math.random() * 0x100000000), board?: ModeRun
): GameState => {
  const settings = normalizeSettings(options);
  const props = settings.mode === 'challenge' ? { remove: 0, undo: 0, shuffle: 0, revive: 0 } : { ...(balances || settings.props) };
  const layout = board ? resolvePatternIndex(board.layoutIndex ?? seed, board.layoutId) : undefined;
  return { ...createIdleState(), runId: generateId(10), level, phase: 'playing',
    cards: board ? checkCover(buildModeCards(board.mode, cardTypes, generateId(8), seededRandom(seed), settings.difficulty, layout!))
      : generateCards(level, cardTypes, seededRandom(seed), settings.difficulty),
    boardMode: board?.mode, dayKey: board?.dayKey, worldStage: board?.stage,
    layoutIndex: layout, layoutId: layout === undefined ? undefined : BOARD_PATTERNS[layout].id,
    props, initialProps: { ...props }, settings, seed, lastResumeAt: now ?? null };
};
export const startModeRun = (mode: BoardMode, types: string[], now: number, settings = DEFAULT_SETTINGS, stage = 1, layoutIndex?: number): GameState => {
  const dayKey = chinaDateKey(now), worldStage = Math.max(1, Math.floor(stage));
  const layout = normalizePatternIndex(layoutIndex ?? (modeSeed(mode, dayKey, 1, 'normal') % BOARD_PATTERNS.length + worldStage - 1));
  return startRun(mode === 'daily' ? 2 : 4, types, now, undefined, settings,
    modeSeed(mode, dayKey, worldStage, settings.difficulty), { mode, dayKey, stage: worldStage, layoutIndex: layout });
};
/** Commit each accepted tap immediately; independent flights present the result in order. */
export const applyMove = (s: GameState, cardId: string, now?: number): GameState => {
  if (s.phase !== 'playing') return s;
  const target = s.cards.find(c => c.id === cardId);
  if (!target || (target.status !== 0 && target.status !== 3) || (target.status === 0 && target.isCover) || s.slotCardIds.length >= SLOT_SIZE) return s;
  if (target.status === 3 && !reserveStacksOf(s.reserveCardIds, s.reserveGroups, s.reserveColumns).some(stack => stack[stack.length - 1] === cardId)) return s;
  const timed = now === undefined ? s : syncClock(s, now);
  let cards = s.cards.map(c => c.id === cardId ? { ...c, status: 1, isCover: false } : c);
  const typeOf = new Map(cards.map(c => [c.id, c.type]));
  const ids = [...s.slotCardIds];
  const lastSame = ids.reduce((last, id, i) => typeOf.get(id) === target.type ? i : last, -1);
  ids.splice(lastSame < 0 ? ids.length : lastSame + 1, 0, cardId);
  const matching = ids.filter(id => typeOf.get(id) === target.type);
  const removed = new Set(matching.length >= 3 ? matching.slice(0, 3) : []);
  if (removed.size) cards = cards.map(c => removed.has(c.id) ? { ...c, status: 2, isCover: false } : c);
  const next: GameState = {
    ...timed, cards: checkCover(cards), slotCardIds: ids.filter(id => !removed.has(id)),
    reserveCardIds: s.reserveCardIds.filter(id => id !== cardId),
    reserveGroups: reserveGroupsOf(s.reserveCardIds, s.reserveGroups).map(group => group.filter(id => id !== cardId)).filter(group => group.length),
    reserveColumns: Object.fromEntries(Object.entries(reserveColumnsOf(s.reserveCardIds, s.reserveGroups, s.reserveColumns)).filter(([id]) => id !== cardId)),
    score: s.score + 10 + (removed.size ? 100 : 0),
    history: [...s.history, snapshotOf(s)].slice(-10), revision: s.revision + 1
  };
  if (next.cards.every(c => c.status === 2)) return {
    ...next, phase: 'won', lastResumeAt: null, settledOutcome: 'win',
    lastSettledRun: { runId: next.runId, outcome: 'win', level: next.level, score: next.score, elapsedMs: next.elapsedMs,
      boardMode: next.boardMode, dayKey: next.dayKey, worldStage: next.worldStage }
  };
  if (next.slotCardIds.length >= SLOT_SIZE) return { ...next, phase: 'failed', failSnapshot: snapshotOf(s), lastResumeAt: null };
  return next;
};
export const undoMove = (s: GameState): GameState => {
  if (s.phase !== 'playing' || s.settings.mode === 'challenge' || !s.history.length || s.props.undo <= 0) return s;
  return { ...s, ...restoreSnapshot(s.history[s.history.length - 1]), props: { ...s.props, undo: s.props.undo - 1 },
    history: s.history.slice(0, -1), revision: s.revision + 1 };
};
export const shuffleBoard = (s: GameState, rng: () => number = Math.random): GameState => {
  if (s.phase !== 'playing' || s.settings.mode === 'challenge' || s.props.shuffle <= 0) return s;
  const field = s.cards.filter(c => c.status === 0);
  const types = field.map(c => c.type);
  if (new Set(types).size < 2) return s;
  const shuffled = shuffledWithRng(types, rng);
  if (shuffled.every((type, i) => type === types[i])) return s;
  const byId = new Map(field.map((card, i) => [card.id, shuffled[i]]));
  const cards = s.cards.map(c => byId.has(c.id) ? { ...c, type: byId.get(c.id)!, emoji: byId.get(c.id)! } : { ...c });
  return { ...s, cards: checkCover(cards), props: { ...s.props, shuffle: s.props.shuffle - 1 }, history: [], revision: s.revision + 1 };
};
/** Move up to three visible slot cards into staging, without eliminating them. */
export const removeFromSlot = (s: GameState, revive = false, now?: number): GameState => {
  const kind = revive ? 'revive' : 'remove';
  if (s.settings.mode === 'challenge' || s.props[kind] <= 0 || !s.slotCardIds.length || s.phase !== (revive ? 'failed' : 'playing')) return s;
  const movedIds = s.slotCardIds.slice(0, 3);
  const moved = new Set(movedIds);
  return { ...s, cards: s.cards.map(c => moved.has(c.id) ? { ...c, status: 3, isCover: false } : { ...c }),
    slotCardIds: s.slotCardIds.slice(movedIds.length), reserveCardIds: [...s.reserveCardIds, ...movedIds],
    reserveGroups: [...reserveGroupsOf(s.reserveCardIds, s.reserveGroups), movedIds],
    reserveColumns: { ...reserveColumnsOf(s.reserveCardIds, s.reserveGroups, s.reserveColumns), ...Object.fromEntries(movedIds.map((id, column) => [id, column])) },
    props: { ...s.props, [kind]: s.props[kind] - 1 }, history: [], failSnapshot: null,
    phase: 'playing', revision: s.revision + 1, lastResumeAt: revive ? now ?? null : s.lastResumeAt };
};
export const reviveRun = (s: GameState, now?: number): GameState => removeFromSlot(s, true, now);
export const pauseRun = (s: GameState, now: number): GameState =>
  s.phase === 'playing' ? { ...syncClock(s, now), phase: 'paused', lastResumeAt: null } : s;
export const resumeRun = (s: GameState, now: number): GameState =>
  s.phase === 'paused' ? { ...s, phase: 'playing', lastResumeAt: now } : s;
export const tickTime = (s: GameState, now: number): GameState => syncClock(s, now);
const quitNotification = (s: GameState): SettledRun | null => {
  const phase = s.phase === 'quiz' ? s.quiz?.returnPhase : s.phase;
  const outcome = phase === 'failed' ? 'fail' : phase === 'playing' || phase === 'paused' ? 'abandon' : null;
  if (!outcome || s.settledOutcome) return s.lastSettledRun;
  return { runId: s.runId, outcome, level: s.level, score: s.score, elapsedMs: s.elapsedMs,
    boardMode: s.boardMode, dayKey: s.dayKey, worldStage: s.worldStage };
};
export const endRun = (s: GameState, now?: number): GameState => {
  if (s.phase === 'ready') return s;
  return { ...createIdleState(), lastSettledRun: quitNotification(now === undefined ? s : syncClock(s, now)) };
};
export const restartRun = (
  s: GameState, cardTypes: string[], now?: number, level = s.level, balances = s.initialProps, settings = s.settings, changeLayout = false
): GameState => {
  const change = changeLayout && s.boardMode === 'world';
  const seed = change ? (Math.imul(s.seed ^ 0x9e3779b9, 1664525) + 1013904223) >>> 0 : s.seed;
  const next = startRun(level, cardTypes, now, balances, settings, s.boardMode ? seed : undefined,
    s.boardMode ? { mode: s.boardMode, dayKey: s.dayKey!, stage: s.worldStage || 1,
      layoutIndex: change ? (s.layoutId ? resolvePatternIndex(s.layoutIndex ?? 0, s.layoutId) + 1 : 0) : s.layoutIndex,
      layoutId: change ? undefined : s.layoutId } : undefined);
  if (s.boardMode && !change && !s.layoutId) {
    // Retired reference saves must keep their own geometry on an explicit same-map restart.
    const counts = new Map<string, number>(); s.cards.forEach(c => counts.set(c.type, (counts.get(c.type) || 0) + 1));
    next.cards = checkCover(buildModeCards(s.boardMode, cardTypes, generateId(8), seededRandom(seed), settings.difficulty, 0, {
      positions: s.cards.map(({ x, y, zIndex, layer, zone, size, sourceId }) => ({ x, y, zIndex, layer: layer ?? zIndex, zone: zone ?? 'main', size, sourceId })),
      typeCounts: [...counts]
    }));
    next.layoutId = undefined; next.layoutIndex = s.layoutIndex;
  }
  return { ...next, lastSettledRun: quitNotification(now === undefined ? s : syncClock(s, now)) };
};
export const canUseProp = (s: GameState, kind: PropKind): boolean => {
  if (s.settings.mode === 'challenge' || s.phase !== (kind === 'revive' ? 'failed' : 'playing')) return false;
  if (kind === 'undo') return s.history.length > 0;
  if (kind === 'remove' || kind === 'revive') return s.slotCardIds.length > 0;
  return new Set(s.cards.filter(c => c.status === 0).map(c => c.type)).size > 1;
};
const performProp = (s: GameState, kind: PropKind, now: number): GameState => {
  if (kind === 'remove') return removeFromSlot(s);
  if (kind === 'undo') return undoMove(s);
  if (kind === 'revive') return reviveRun(s, now);
  const shuffled = shuffleBoard(s, seededRandom(s.seed + s.revision * 31 + now));
  if (shuffled !== s) return shuffled;
  const field = s.cards.filter(c => c.status === 0);
  const other = field.findIndex(c => c.type !== field[0].type);
  let step = 0;
  return shuffleBoard(s, () => step++ === 0 ? (other + .01) / field.length : 0);
};
export const canRequestProp = (s: GameState, kind: PropKind): boolean =>
  canUseProp(s, kind) && (s.props[kind] > 0 || (s.settings.quizMode !== 'off' && quizGrantUsage(s.quizStats)[kind] < quizGrantLimit(s.settings, kind)));
export const requestProp = (s: GameState, kind: PropKind, now: number): GameState => {
  if (!canRequestProp(s, kind)) return s;
  if (s.props[kind] > 0 && s.settings.quizMode !== 'always') return performProp(s, kind, now);
  const pool = questionsIn(s.settings.questionCategory, s.settings.mathTopic);
  if (!pool.length) return s;
  const timed = syncClock(s, now);
  return { ...timed, phase: 'quiz', lastResumeAt: null, revision: s.revision + 1, quiz: {
    id: s.runId + '-' + (s.revision + 1) + '-' + s.quizStats.completed,
    runId: s.runId, revision: s.revision + 1, kind,
    questionId: pool[(s.seed + s.revision + s.quizStats.completed) % pool.length].id,
    returnPhase: kind === 'revive' ? 'failed' : 'playing', needsGrant: s.props[kind] <= 0,
    attempts: 0, selected: null, feedback: null, round: 0
  } };
};
export const cancelQuiz = (s: GameState, now: number): GameState => {
  if (s.phase !== 'quiz' || !s.quiz) return s;
  const phase = s.quiz.returnPhase;
  return { ...s, phase, quiz: null, lastResumeAt: phase === 'playing' ? now : null };
};
export const changeQuestion = (s: GameState, requestId: string): GameState => {
  if (s.phase !== 'quiz' || !s.quiz || s.quiz.id !== requestId || s.quiz.feedback !== 'wrong') return s;
  const pool = questionsIn(s.settings.questionCategory, s.settings.mathTopic);
  if (pool.length < 2) return s;
  const index = pool.findIndex(q => q.id === s.quiz!.questionId);
  return { ...s, quiz: { ...s.quiz, id: s.runId + '-' + s.revision + '-q' + (s.quiz.round + 1), questionId: pool[(index + 1) % pool.length].id,
    attempts: 0, selected: null, feedback: null, round: s.quiz.round + 1 } };
};
export const answerQuiz = (s: GameState, requestId: string, selected: number, _now: number, questionId?: string): GameState => {
  const quiz = s.quiz;
  if (s.phase !== 'quiz' || !quiz || quiz.id !== requestId || quiz.runId !== s.runId || quiz.revision !== s.revision) return s;
  if (quiz.selected !== null || quiz.attempts > 0) return s;
  if (questionId && quiz.questionId !== questionId) return s;
  const question = QUESTIONS.find(q => q.id === quiz.questionId);
  if (!question || !Number.isInteger(selected) || selected < 0 || selected >= question.options.length) return s;
  const correct = selected === question.answer;
  const stats = { ...s.quizStats, firstAttempts: s.quizStats.firstAttempts + 1, firstCorrect: s.quizStats.firstCorrect + (correct ? 1 : 0) };
  return { ...s, quizStats: stats, quiz: { ...quiz, attempts: 1, selected, feedback: correct ? 'correct' : 'wrong' } };
};
export const claimQuiz = (s: GameState, requestId: string, now: number): GameState => {
  const quiz = s.quiz;
  if (s.phase !== 'quiz' || !quiz || quiz.id !== requestId || quiz.runId !== s.runId || quiz.revision !== s.revision || quiz.feedback !== 'correct') return s;
  const question = QUESTIONS.find(q => q.id === quiz.questionId);
  if (!question || quiz.selected !== question.answer || s.settings.mode === 'challenge') return s;
  const usage = quizGrantUsage(s.quizStats), kind = quiz.kind;
  if (quiz.needsGrant && usage[kind] >= quizGrantLimit(s.settings, kind)) return s;
  const stats = { ...s.quizStats, grantsByKind: usage };
  stats.completed++;
  if (quiz.needsGrant) { stats.grants++; stats.grantsByKind[kind]++; }
  let next: GameState = { ...s, phase: quiz.returnPhase, quiz: null, quizStats: stats,
    lastResumeAt: quiz.returnPhase === 'playing' ? now : null,
    props: { ...s.props, [quiz.kind]: s.props[quiz.kind] + (quiz.needsGrant ? 1 : 0) } };
  if (quiz.kind === 'revive' || s.settings.quizMode === 'always') next = performProp(next, quiz.kind, now);
  return next;
};
export const getSlotCards = (s: GameState): Card[] =>
  s.slotCardIds.map(id => s.cards.find(c => c.id === id)).filter((c): c is Card => !!c);
export const getReserveCards = (s: GameState): Card[] =>
  s.reserveCardIds.map(id => s.cards.find(c => c.id === id)).filter((c): c is Card => !!c);
export const checkInvariants = (s: GameState): boolean => {
  if (new Set(s.cards.map(c => c.id)).size !== s.cards.length) return false;
  for (const [status, ids] of [[1, s.slotCardIds], [3, s.reserveCardIds]] as [number, string[]][]) {
    if (new Set(ids).size !== ids.length) return false;
    const actual = s.cards.filter(c => c.status === status).map(c => c.id).sort();
    const expected = [...ids].sort();
    if (actual.length !== expected.length || actual.some((id, i) => id !== expected[i])) return false;
  }
  return !s.slotCardIds.some(id => s.reserveCardIds.includes(id)) && s.slotCardIds.length <= SLOT_SIZE;
};
